import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import clsx from 'clsx'
import _ from 'lodash'
import i18n from '@pureartisan/simple-i18n'
import { makeStyles } from '@material-ui/core/styles'
import {
  Box,
  ButtonBase,
  Link,
  Typography,
} from '@material-ui/core'
import { NavigationControl } from 'react-map-gl'
import Mapbox, {
  useMapStore,
  useMapViewport,
  useFlyToBounds,
  useFlyToFeature,
  useFlyToLatLon,
  useFlyToReset,
  useFlyToState,
} from '@hyperobjekt/mapbox'
import { fromJS, set } from 'immutable'
import shallow from 'zustand/shallow'
import { isMobile } from 'react-device-detect'

import useStore from './../store'
import { theme } from './../theme'
import {
  DEFAULT_VIEWPORT,
  MAP_CONTROLS_CLASSES,
  FULL_FUNCT_ZOOM_THRESHOLD,
  DEFAULT_ACTIVE_SHAPE,
} from './../../../../constants/map'
import { defaultMapStyle } from './utils/selectors'
import { getLayers } from './utils/layers'
import { getSources } from './utils/sources'
import {
  getStateUpdateForPOI,
  isExplorerView,
  useDebounce,
  usePrevious,
} from './../utils'
import MapPopup from './components/MapPopup'
import MoreControlsContainer from './components/MoreControlsContainer'
import { getIsControl } from './../utils'
const poi_data = require('../../../../constants/poi_data.json')

const normShapeLayerMap = {
  s: 'states',
  m: 'metros',
}

const useStyles = makeStyles(theme => ({
  parent: {
    position: 'absolute',
    top: `${theme.mixins.toolbar.minHeight}px`,
    bottom: 0,
    left: 0,
    right: 0,
    // prevent map from creating scrollbars
    overflow: 'hidden',
    [theme.breakpoints.up('sm')]: {
      height: `calc(100vh - ${theme.mixins.toolbar['@media (min-width:600px)'].minHeight}px)`,
      top: `${theme.mixins.toolbar['@media (min-width:600px)'].minHeight}px`,
      left: `${theme.extras.controlPanel.width}px`,
      width: `calc(100vw - ${theme.extras.controlPanel.width}px)`,
    },
    // make attribute row fit mobile screen
    [theme.breakpoints.down('xs')]: {
      '& .mapboxgl-ctrl-bottom-left': {
        width: 29,
        overflow: 'hidden',
      },
      '& .divider.first': {
        display: 'none',
      },
      '& .custom-attribution': {
        left: '30px',
      },
    },

    '& .divider': {
      color: theme.extras.variables.colors.lightGray,
      fontWeight: 'lighter',
    },
  },
  embed: {
    height: `100vh`,
    top: 0,
    [theme.breakpoints.up('md')]: {
      height: `100vh`,
      top: 0,
      left: 0,
      width: `100vw`,
    },
  },
  navControls: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
    position: 'absolute',
    right: '16px',
    bottom: '16px',
    '&>div': {
      position: 'relative !important',
    },
    '& .mapboxgl-ctrl.mapboxgl-ctrl-group': {
      borderRadius: 0,
      '& .mapboxgl-ctrl-icon': {
        width: '18px',
        height: '18px',
        margin: 'auto',
        fontWeight: 200,
      },
      '& button.mapboxgl-ctrl-zoom-in': {
        width: '28px', // '32px',
        height: '28px', // '32px',
        '& .mapboxgl-ctrl-icon': {
          backgroundImage: `url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAABkSURBVHgB7ZSxCoAwEENz1n/ooEP9/4+yi4MfIZHiIjrFIlK8B7cchJAjHOA0RYjDXEbR9JCwBJEOL+MG3xvYdXHU0BIewbyty3Te3BKwIhRrDxLiyDKKxlv0AwPpmxKW4TTHDu+rDrVuNRmMAAAAAElFTkSuQmCC)`,
        },
      },
      '& .mapboxgl-ctrl-zoom-out': {
        width: '28px', // '32px',
        height: '28px', // '32px',
        '& .mapboxgl-ctrl-icon': {
          backgroundImage: `url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAA/SURBVHgB7cyxEQAQEETRRRECCf0XRSJQhEFoXMAQ2hf+uVuAiGhLrcFYF0f2uNJSLTnMRYsTmc7nH36J6GsdzSMIBsOVRsEAAAAASUVORK5CYII=)`,
        },
      },
    },
  },
  customAttrib: {
    position: 'absolute',
    left: '100px',
    bottom: '6px',
  },
  customAttribTxt: {
    marginLeft: '0.3rem',
    marginRight: '0.3rem',
    verticalAlign: 'middle',
    fontWeight: 400,
    '& .MuiTypography-root': {
      fontWeight: 400,
    },
  },
  mobileGate: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'initial',
    },
  },
}))

const BaseMap = ({ ...props }) => {
  // Values from store.
  const {
    activeView,
    activeYear,
    dataVersion,
    activePointLayers,
    activeRedliningTypes,
    showDensityOverlays,
    activeMetric,
    activeNorm,
    POIModeActive,
    activePOI,
    loadFromPOIUrl,
    activeRegion,
    activeShape,
    setStoreValues,
    setActivePOI,
    slideoutPanel,
    slideoutFeature,
    showZipBoundaries,
    redliningActive,
    activeGeoType,
    activeRatingType,
    hoveredFeatureId,
    flyToFeature,
    flyToBounds,
    hoveredFeatureIdArr,
    pushHoveredFeatureId,
    showIntroModal,
    flyToReset,
    breakpoint,
    windowInnerHeight,
    flyingToLocation,
    mapSize,
    mapInteractionState,
  } = useStore(
    state => ({
      activeView: state.activeView,
      loadYears: state.loadYears,
      activeYear: state.activeYear,
      dataVersion: state.dataVersion,
      activePointLayers: state.activePointLayers,
      activeRedliningTypes: state.activeRedliningTypes,
      showDensityOverlays: state.showDensityOverlays,
      activeMetric: state.activeMetric,
      activeNorm: state.activeNorm,
      activeRegion: state.activeRegion,
      POIModeActive: state.POIModeActive,
      activePOI: state.activePOI,
      loadFromPOIUrl: state.loadFromPOIUrl,
      activeShape: state.activeShape,
      setStoreValues: state.setStoreValues,
      setActivePOI: state.setActivePOI,
      slideoutPanel: state.slideoutPanel,
      slideoutFeature: state.slideoutFeature,
      showZipBoundaries: state.showZipBoundaries,
      redliningActive: state.redliningActive,
      activeGeoType: state.activeGeoType,
      activeRatingType: state.activeRatingType,
      hoveredFeatureId: state.hoveredFeatureId,
      flyToFeature: state.flyToFeature,
      flyToBounds: state.flyToBounds,
      hoveredFeatureIdArr: state.hoveredFeatureIdArr,
      pushHoveredFeatureId: state.pushHoveredFeatureId,
      showIntroModal: state.showIntroModal,
      flyToReset: state.flyToReset,
      breakpoint: state.breakpoint,
      windowInnerHeight: state.windowInnerHeight,
      flyingToLocation: state.flyingToLocation,
      mapSize: state.mapSize,
      mapInteractionState: state.mapInteractionState,
    }),
    shallow,
  )

  //// --- SETUP ---

  // Token and viewport passed to the map.
  const token = process.env.MAPBOX_API_TOKEN

  const [loaded, setLoaded] = useState(false)

  const mapRef = useRef(null)
  const [localMapRef, setLocalMapRef] = useState(null)
  useEffect(() => {
    if (mapRef.current) {
      // gives you access to StaticMap methods from ReactMapGL
      // https://visgl.github.io/react-map-gl/docs/api-reference/static-map#methods
      const map = mapRef.current.getMap()

      // TODO: are these successfully disabling?
      map.dragRotate.disable()
      map.touchZoomRotate.disableRotation()
      map.touchPitch.disable()
      // do something with the map
      setLocalMapRef(map)
      // HACK: give access to map to other components
      window.localMapRef = map
    }
  }, [mapRef.current])

  // useEffect(() => {
  //   if (!!loaded && !!localMapRef) {
  //     localMapRef.moveLayer('settlement-minor-label')
  //     localMapRef.moveLayer('settlement-major-label')
  //   }
  // }, [loaded])

  // storing previous hover / selected IDs
  // const prev = usePrevious({
  //   hoveredFeatureId,
  //   activeShape,
  //   activeGeoType,
  //   activeNorm,
  // })

  const classes = useStyles()

  setStoreValues({
    flyToFeature: useFlyToFeature(),
    flyToBounds: useFlyToBounds(),
    flyToLatLon: useFlyToLatLon(),
    flyToState: useFlyToState(),
    flyToReset: useFlyToReset(),
  })

  // --- FUNCTIONS FOR UPDATING MAP ---

  const setFeatureState = (
    featureId,
    geoType,
    attribute,
    value,
  ) => {
    localMapRef.setFeatureState(
      {
        id: featureId,
        source: 'ddkids_' + geoType,
        sourceLayer: geoType,
      },
      { [attribute]: value },
    )
  }

  /**
   * HACK: Clears hovered state from old hovered items (cleanup for fast mouse movement).
   */
  const resetFeatureHoverStates = () => {
    const features = localMapRef
      .queryRenderedFeatures({
        layers: [`${activeGeoType}Shapes`],
      })
      .filter(el => el.state.hovered === true)

    for (var i = 0; i < features.length; i++) {
      if (features[i].id !== activeShape) {
        setFeatureState(
          features[i].id,
          activeGeoType,
          'hovered',
          false,
        )
      }
    }
  }

  /**
   * get mapbox features from the active feature layer (tract/zip/county) corresponding to id
   * @param Number feature (tract, zip, or county)
   */
  const getSourceFeatures = id =>
    localMapRef.querySourceFeatures(
      `ddkids_${activeGeoType}`,
      {
        sourceLayer: `${activeGeoType}`,
        filter: ['==', ['id'], id],
      },
    )

  /*
   * clear mapbox active state and internal selection state
   */
  const clearActiveFeature = () => {
    setFeatureState(
      activeShape,
      activeGeoType,
      'active',
      false,
    )

    const values = {
      activeShape: DEFAULT_ACTIVE_SHAPE,
      slideoutFeatureId: 0,
      slideoutFeature: null,
    }

    // close panel if opened to the location
    if (slideoutPanel.panel === 'location') {
      values.slideoutPanel = { active: false }
    }

    setStoreValues(values)
  }
  window.clearActiveMapFeature = clearActiveFeature

  /**
   * set mapbox active state and internal selection state for clicked feature
   * @param {object} feature (tract, zip, or county)
   */
  const highlightFeature = (feature, options = {}) => {
    if (!feature) return

    setFeatureState(
      activeShape,
      activeGeoType,
      'active',
      false,
    )
    setFeatureState(
      feature.id,
      activeGeoType,
      'active',
      true,
    )
    const values = {
      activeShape: feature.id,
      slideoutFeatureId: feature.id,
      slideoutFeature: feature,
    }

    if (options.openLocationDetailsPanel) {
      values.slideoutPanel = {
        panel: 'location',
        active: true,
      }
    }

    setStoreValues(values)
  }

  /*
   * highlights the feature (tract, zip, or county) at screen center
   */
  const highlightCenteredFeature = (options = {}) => {
    const features = localMapRef.queryRenderedFeatures(
      [mapSize[0] / 2, mapSize[1] / 2],
      {
        layers: [`${activeGeoType}Shapes`],
      },
    )

    highlightFeature(features[0], options)
  }

  /**
   * activate the metro/state containing the feature, depending on norming mode
   * @param {object} feature (tract, zip, or county)
   * @return {boolean} whether a region is activated
   */
  const activateContainingRegion = feature => {
    if (activeNorm === 'm') {
      // note that if the metro isn't in the top 100, none will appear active
      setStoreValues({
        activeRegion: feature.properties.m,
      })
      return true
    } else if (activeNorm === 's') {
      setStoreValues({
        activeRegion: feature.properties.s,
      })
      return true
    }

    return false
  }

  /**
   * activates a particular region (metro or state) at the screen center
   * @param Function callback to be called once region is activated (set to store)
   */
  const activateCenteredRegion = (callback = _.noop) => {
    const normShapeLayer = normShapeLayerMap[activeNorm]

    const normFeatures =
      normShapeLayer &&
      localMapRef.queryRenderedFeatures(
        [mapSize[0] / 2, mapSize[1] / 2],
        {
          layers: [`${normShapeLayer}Shapes`],
        },
      )

    if (normFeatures && normFeatures[0]) {
      setStoreValues({ activeRegion: normFeatures[0].id })
      setTimeout(() => callback(), 1000)
    } else {
      console.log('no region to activate')
      callback()
    }
  }

  // --- SIDE EFFECTS ---

  // remove tract/zip selection state when toggling geoType
  // NOTE: Instead use function on window object to clearActiveMapFeature before geoType changes
  // useEffect(() => {
  //   if (!!loaded) clearActiveFeature()
  // }, [activeGeoType])

  // when norm is changed, update activeRegion to keep active feature visible
  useEffect(() => {
    if (!slideoutFeature) {
      return
    }

    const isInTop100 =
      // i = 1 if location belongs to the 100 metros examined, else 0
      slideoutFeature.properties.i ||
      // zips hold metro on m, only for top metros
      slideoutFeature.properties.m

    if (activeNorm === 'm' && !isInTop100) {
      // fixes 83 - clear active feature if no longer visible
      clearActiveFeature()
    } else {
      activateContainingRegion(slideoutFeature)
    }
  }, [activeNorm])

  // HACK: remove feature hover states every so often to ensure none stay stuck
  useEffect(() => {
    if (!!loaded && hoveredFeatureIdArr.length > 30) {
      setStoreValues({ hoveredFeatureIdArr: [] })
      resetFeatureHoverStates()
    }
  }, [hoveredFeatureIdArr])

  // when source reloads, ensure proper feature highlight/clear.
  // Necessary because different tilesets are loaded for different years.
  // Some may not have data attached to them.
  // Restore selected state to the same feature in the selected year tileset,
  // if it has data.
  useEffect(() => {
    if (!loaded || !localMapRef || !activeShape) return
    // give the map a chance to load
    setTimeout(() => {
      const feature = getSourceFeatures(activeShape)[0]

      // If there is a feature with data, set as active feature. Otherwise clear.
      if (!!feature) {
        highlightFeature(feature)
      } else {
        // console.log('No feature with data found.')
        clearActiveFeature()
      }
    }, 1000)
  }, [activeYear])

  // --- INTERACTION AND EVENT HANDLERS ---

  const handleLoad = () => {
    setLoaded(true)
    document.dispatchEvent(new Event('mapDataLoaded'))
    // Put place labels on top.
    localMapRef.moveLayer('settlement-minor-label')
    localMapRef.moveLayer('settlement-major-label')
    // Set map dimensions
    handleResize()

    // If initial load, reset the map to viewport.
    if (!!showIntroModal) flyToReset()

    if (loadFromPOIUrl) {
      const poi = poi_data.sites.find(
        p => p.id === activePOI,
      )

      setTimeout(() => flyToBounds(poi.bbox), 1000)
    }

    // If there is an active shape inherited from the hash, activate its region
    // and highlight it (which sets slideoutFeature and mapbox featureState)

    // time (in ms) after which to give up attempting to load
    let loadFeaturePatience = 10 * 1000
    const loadInterval = 500
    const loadActiveFeature = () => {
      const feature = getSourceFeatures(activeShape)[0]
      if (feature) {
        activateContainingRegion(feature)
        highlightFeature(feature)
      } else if (loadFeaturePatience > 0) {
        // features won't be available immediately, so try again shortly
        loadFeaturePatience -= loadInterval
        setTimeout(loadActiveFeature, loadInterval)
      } else {
        // we've exceeded our max wait, perhaps the feature with that id doesn't exist
        console.log(
          `can't find feature for active shape from hash: ${activeShape}`,
        )
      }
    }

    !!activeShape && loadActiveFeature()
  }

  const handleClick = e => {
    const isControl = getIsControl(e)
    if (
      activeView === 'embed' ||
      !!isControl ||
      !!mapInteractionState
    ) {
      return
    }
    // Get the clicked feature.
    const features = localMapRef.queryRenderedFeatures(
      e.point,
      {
        layers: [`${activeGeoType}Shapes`],
      },
    )
    const shapeClicked = !!features && features.length > 0
    if (shapeClicked) {
      // We have a feature.
      const feature = features[0]

      // Is the feature the same as the current active feature?
      if (feature.id === activeShape) {
        clearActiveFeature()
      } else {
        highlightFeature(feature, {
          openLocationDetailsPanel: true,
        })
      }
    }

    // whether a feature was clicked or a region was activated, the region
    // (or containing region) will be flown to (if zoom level is appropriate)
    const zoomedOut =
      viewport.zoom < FULL_FUNCT_ZOOM_THRESHOLD

    if (POIModeActive) {
      const poiFeatures = localMapRef.queryRenderedFeatures(
        e.point,
        {
          layers: [`poisShapes`],
        },
      )
      const id = _.get(poiFeatures, '[0].properties.p')
      let values = {}
      if (id) {
        if (id === activePOI) return

        if (zoomedOut) {
          const poi = poi_data.sites.find(p => p.id === id)
          flyToBounds(poi.bbox)
        }

        if (slideoutPanel.panel === 'location') {
          values.slideoutPanel = { active: false }
        }
        values = {
          ...values,
          ...getStateUpdateForPOI(id, activeNorm),
        }

        if (Object.keys(values).length > 0) {
          setStoreValues(values)
        }
        setActivePOI(id)
        clearActiveFeature()
      }

      return
    }

    // The following behaviors only apply to m/s norming modes
    const normShapeLayer = normShapeLayerMap[activeNorm]

    if (!normShapeLayer) return

    // if a feature was not just clicked, the click will activate that state/metro
    const activateRegion = !shapeClicked

    if (!activateRegion && !zoomedOut) return

    const normRegionFeatures = localMapRef.queryRenderedFeatures(
      e.point,
      {
        layers: [`${normShapeLayer}Shapes`],
      },
    )

    if (!!normRegionFeatures && !!normRegionFeatures[0]) {
      zoomedOut && flyToFeature(normRegionFeatures[0])

      if (activateRegion) {
        const values = {
          activeRegion: normRegionFeatures[0].id,
        }
        if (slideoutPanel.panel === 'location') {
          values.slideoutPanel = { active: false }
        }
        setStoreValues(values)
        clearActiveFeature()
      }
    }
  }

  const handleMouseOut = e => {
    if (activeView === 'embed') {
      return
    }
    // When the users mouses out of the map canvas,
    // reset the hovered feature values and feature states.
    setFeatureState(
      hoveredFeatureId,
      activeGeoType,
      'hovered',
      false,
    )
    setStoreValues({
      hoveredFeatureId: 0,
      hoveredFeature: null,
    })
  }

  const handleMouseMove = e => {
    if (activeView === 'embed' || !!mapInteractionState) {
      return
    }

    const isControl = getIsControl(e)

    // If hovering a control, remove currently hovered.
    if (!!isControl) {
      setFeatureState(
        hoveredFeatureId,
        activeGeoType,
        'hovered',
        false,
      )
      setStoreValues({
        hoveredFeatureId: 0,
        hoveredFeature: null,
      })
      return
    }

    const features = localMapRef.queryRenderedFeatures(
      e.point,
      {
        layers: [
          `${activeGeoType}Shapes`,
          `${activeGeoType}Lines`,
        ],
      },
    )

    // If mouse moved outside any features, remove hovered state
    if (features.length === 0) {
      setFeatureState(
        hoveredFeatureId,
        activeGeoType,
        'hovered',
        false,
      )
      setStoreValues({
        hoveredFeatureId: 0,
        hoveredFeature: null,
      })
      return
    }

    // Track mouse coords and xy for use by map tooltip.
    const updates = {
      mouseXY: e.point,
      coords: e.lngLat,
    }

    // If hovering a new feature, update to it.
    if (features[0].id !== hoveredFeatureId) {
      pushHoveredFeatureId(features[0].id)
      setFeatureState(
        hoveredFeatureId,
        activeGeoType,
        'hovered',
        false,
      )
      setFeatureState(
        features[0].id,
        activeGeoType,
        'hovered',
        true,
      )

      setStoreValues({
        ...updates,
        hoveredFeatureId: features[0].id,
        hoveredFeature: features[0],
      })
      return
    } else {
      // If hovering the same feature, just update mouse position.
      setStoreValues(updates)
    }
  }

  /*
   * Handles region activation and feature highlight after a fly-to
   */
  const handleTransitionEnd = () => {
    if (!flyingToLocation) return
    setStoreValues({ flyingToLocation: false })

    // only highlight the centered feature if the transition is from flying to a location
    activateCenteredRegion(
      flyingToLocation.highlightCentered
        ? highlightCenteredFeature
        : _.noop,
    )
  }

  const getMapDimensions = () => {
    const map = document.getElementById('map')
    return [map.offsetWidth, map.offsetHeight]
  }

  const handleResize = () => {
    // Set map dimensions
    setStoreValues({
      mapSize: getMapDimensions(),
    })
  }

  const handleInteractionStateChange = e => {
    const state =
      e.inTransition ||
      e.isDragging ||
      e.isPanning ||
      e.isRotating ||
      e.isZooming
        ? true
        : false

    // console.log({
    //   mapInteractionState: state,
    // })
    setStoreValues({
      mapInteractionState: state,
    })
  }

  const openCitationFaq = () => {
    const faqId = 'citation'
    setStoreValues({
      slideoutPanel: {
        active: true,
        panel: 'faq',
      },
      expandedFaqs: { [faqId]: true },
    })

    setTimeout(() => {
      // scroll to question, whether in modal or slideout panel
      const elem = document.querySelector(`#faq-${faqId}`)
      elem && elem.scrollIntoView({ behavior: 'smooth' })
    })
  }

  // --- MAP RENDER HELPERS ---

  const viewport = useStore(state => state.viewport)
  const setViewport = useStore(state => state.setViewport)
  // handler for viewport change, debounced to prevent
  // race errors
  const resetViewportState = useCallback(
    (vp, options = {}) => {
      if (!loaded) return
      setViewport(vp)
    },
    [setViewport, loaded],
  )

  // These are for updating our own app state (for hash management).
  const mapViewport = useMapViewport()
  const debouncedMapViewport = useDebounce(mapViewport, 200)
  useEffect(() => {
    resetViewportState(mapViewport[0])
  }, [debouncedMapViewport])

  // This is from the mapbox component.
  const setMapViewport = useMapStore(
    state => state.setViewport,
  )
  // This is to update the map component state.
  const handleViewportChange = vp => {
    setMapViewport(vp)
  }

  /** memoized array of shape and point layers */
  const layers = useMemo(() => {
    if (!loaded || !activeMetric || !activeNorm) {
      return []
    }
    const context = {
      activeYear,
      activeMetric,
      activeNorm,
      activeRegion,
      POIModeActive,
      activePOI,
      activePointLayers,
      activeRedliningTypes,
      showDensityOverlays,
      showZipBoundaries,
      showRedlining:
        activeGeoType === 'tracts' && redliningActive,
      activeRatingType,
      activeGeoType,
      viewport,
    }
    return getLayers(context)
  }, [
    loaded,
    activeYear,
    activeMetric,
    activeNorm,
    activePointLayers,
    activeRedliningTypes,
    activeRegion,
    POIModeActive,
    activePOI,
    showDensityOverlays,
    showZipBoundaries,
    redliningActive,
    activeGeoType,
    activeRatingType,
    viewport.zoom,
  ])

  /**
   * Returns the map style with the provided layers inserted
   * @param {Map} style immutable Map of the base mapboxgl style
   * @param {array} layers list of layer objects containing style and z order
   */
  const getUpdatedMapStyle = (
    style,
    layers,
    sources = fromJS({}),
  ) => {
    const updatedSources = style
      .get('sources')
      .merge(sources)
    const updatedLayers = layers.reduce(
      (newLayers, layer) =>
        newLayers.splice(layer.z, 0, layer.style),
      style.get('layers'),
    )
    return style
      .set('sources', updatedSources)
      .set('layers', updatedLayers)
  }

  // update map style layers when layers change
  const mapStyle = useMemo(
    () =>
      getUpdatedMapStyle(
        defaultMapStyle,
        layers,
        getSources(
          process.env.MAPBOX_USER,
          process.env.MAPBOX_API_TOKEN,
          activeYear,
          dataVersion,
          activeView, // TODO: remove when WKKF uses same years
        ),
      ),
    [defaultMapStyle, layers],
  )

  const height = useMemo(() => {
    return {
      height: `${
        windowInnerHeight -
        theme.mixins.toolbar[
          '@media (min-width:0px) and (orientation: landscape)'
        ].minHeight
      }px`,
    }
  }, [isMobile, breakpoint])

  // Passed through to the MapGL component.
  const mapProps = {
    mapboxApiAccessToken: token,
    minZoom: DEFAULT_VIEWPORT.minZoom,
    maxZoom: DEFAULT_VIEWPORT.maxZoom,
    mapStyle: mapStyle,
    // preserveDrawingBuffer: true,
    scrollZoom: true,
    dragRotate: false,
    doubleClickZoom: false,
    onMouseMove: handleMouseMove,
    onMouseOut: handleMouseOut,
    onResize: handleResize,
    onClick: handleClick,
    onTransitionEnd: handleTransitionEnd,
    onInteractionStateChange: handleInteractionStateChange,
  }

  return (
    <Box
      className={clsx(classes.parent, {
        [classes.embed]: activeView === 'embed',
      })}
      style={!!isMobile ? { ...height } : {}}
    >
      <Mapbox
        ref={mapRef}
        defaultViewport={{
          ...DEFAULT_VIEWPORT,
        }}
        MapGLProps={mapProps}
        style={{ width: '100%', height: '100%' }}
        onLoad={handleLoad}
        maxBounds={DEFAULT_VIEWPORT.maxBounds}
      >
        {
          <>
            <MapPopup />
            <Box
              className={clsx(
                'custom-attribution',
                'screenshot-ignore',
                classes.customAttrib,
              )}
            >
              <span className="divider first">|</span>
              <Typography
                variant="caption"
                className={clsx(classes.customAttribTxt)}
              >
                {i18n.translate(`MAP_UI_POWERED_BY_ICON`)}{' '}
                <Link
                  href={i18n.translate(`MAP_AUTHOR_LINK`)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {i18n.translate(`MAP_AUTHOR`)}
                </Link>
              </Typography>
              <span className="divider">|</span>
              <Typography
                variant="caption"
                className={clsx(classes.customAttribTxt)}
              >
                {i18n.translate(`MAP_UI_POWERED_BY_ICON`)}{' '}
                <Link
                  href={i18n.translate(`MAP_AUTHOR_2_LINK`)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {i18n.translate(`MAP_AUTHOR_2`)}
                </Link>
              </Typography>
              <span className="divider">|</span>
              <ButtonBase
                className={classes.customAttribTxt}
                onClick={openCitationFaq}
              >
                <Typography variant="caption">
                  {i18n.translate(`MAP_UI_CITATION`)}
                </Typography>
              </ButtonBase>
            </Box>
            <Box
              className={clsx(
                classes.navControls,
                'screenshot-ignore',
              )}
            >
              {isExplorerView(activeView) && (
                <>
                  <NavigationControl
                    showCompass={false}
                    onViewportChange={viewport => {
                      handleViewportChange(viewport)
                    }}
                    captureClick={true}
                  ></NavigationControl>
                </>
              )}
            </Box>
            {isExplorerView(activeView) && (
              <>
                <MoreControlsContainer
                  mapRef={localMapRef}
                />
              </>
            )}
          </>
        }
      </Mapbox>
    </Box>
  )
}

BaseMap.propTypes = {}

BaseMap.defaultProps = {}

export default BaseMap
